import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { firstValueFrom, timeout, TimeoutError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AuthService } from './auth.service';
import { LanguageService } from './language.service';

/**
 * Z-report -> Fortnox (fortnoxZApi in pubq-functions). One Fortnox connection per COMPANY (org
 * number), shared by its restaurants; settings and the Z list are per restaurant (route).
 */
export interface FortnoxZState {
    mode: 'mock' | 'readonly' | 'live';
    location: { salesLocationId: string; routeName: string; name: string; clientName: string; clientOrgNo: string | null };
    company: { orgNo: string; companyName: string; status: string; connectedAt: string; connectedBy: string; lastRefreshAt: string | null; lastError: any; migratedFrom: string | null } | null;
    connected: boolean;
    orgMatch: boolean | null;
    settings: {
        autoExport: boolean;
        costCenter: string | null;
        voucherSeries: string | null;
        roundingAccount: string;
        accountMap: { [from: string]: string };
        reviewFirstN: number;
        approvedCount: number;
        exportFromZNo: number | null;
    };
}

export interface FortnoxZLists {
    accounts: string[];
    costCenters: { code: string; description: string }[] | null;
    costCentersUnavailable?: string | null;
    series: { code: string; description: string }[];
}

export interface FortnoxZRow {
    zNo: number;
    businessDate: string;
    total: number | null;
    receipts: [number, number];
    status: 'not_sent' | 'pending' | 'held' | 'sending' | 'sent' | 'failed' | 'legacy';
    fortnox: { series: string; number: number; year: number; foundExisting?: boolean; mode?: string } | null;
    lastError: { code: string; message: string; retryable: boolean } | null;
    sentAt: string | null;
    trigger: string | null;
    autoEligible: boolean;
}

export interface FortnoxZPreview {
    ok: boolean;
    errors: { code: string; message: string }[];
    warnings: { code: string; message: string }[];
    summary: { zNo: number; businessDate: string; debit: number; credit: number; zTotal: number; receipts: number; settlementNos: number[]; dropped: any[] };
    payload: { Voucher: { VoucherSeries: string; TransactionDate: string; Description: string; VoucherRows: { Account: number; Debit: number; Credit: number; TransactionInformation: string; CostCenter?: string }[] } };
}

/**
 * Customer import (consume-only). PubQ reads the restaurant's Fortnox customer register and
 * copies a customer in; it never creates or changes anything in Fortnox.
 *
 * `ready` is the only flag the UI should gate the search box on: a connection made before the
 * `customer` scope existed is `connected: true, ready: false, reason: 'missing_scope'`, and the
 * fix is to run the consent flow again (reconnectUrl on the 409).
 */
export interface FortnoxCustomerSourceStatus {
    id: string;
    label: string;
    connected: boolean;
    ready: boolean;
    reason?: 'not_connected' | 'missing_scope' | 'reauth_required' | string;
}

/** A customer as the source system has it, normalised by the backend. */
export interface ExternalCustomer {
    sourceId: string;
    externalId: string;
    name: string;
    organizationNumber: string;
    vatNumber: string;
    email: string;
    invoiceEmail: string;
    phone: string;
    contactName: string;
    address: { street: string; postalCode: string; city: string; country: string };
    paymentTermsCode: string | null;
    paymentTermsDays: number;
    /** false = Fortnox's terms code was not a day count; 30 days was assumed. Review it. */
    paymentTermsExact: boolean;
    active: boolean;
}

/** An existing PubQ customer the external one might be the same company as. */
export interface FortnoxCustomerCandidate {
    pushKey: string;
    reasons: string[];
    companyName: string;
    organizationNumber: string;
    invoiceEmail: string;
    city: string;
    linkedTo: { provider: string; externalId: string; orgNo: string | null } | null;
}

export type FortnoxCustomerMatch =
    | { kind: 'none' }
    | { kind: 'already_imported'; pushKey: string }
    | { kind: 'org_match'; pushKey: string }
    | { kind: 'conflict'; candidates: FortnoxCustomerCandidate[] };

/** The detail call: the source record, what PubQ would store, and whether we have it already. */
export interface FortnoxCustomerDetail {
    customer: ExternalCustomer;
    /** Exactly the shape of a PubQ corporate customer, minus key/source/createdAt. */
    pubqPreview: {
        legalEntity: { companyName: string; legalName: string; organizationNumber: string; vatNumber: string };
        billingAddress: { street: string; postalCode: string; city: string; country: string };
        invoiceSettings: { deliveryMethod: string; invoiceEmail: string; paymentTermsDays: number };
        billingContact: { name: string; email: string; phone: string };
        status?: string;
    };
    match: FortnoxCustomerMatch;
}

/**
 * The outcome of an import.
 *
 *   created           a new PubQ customer
 *   linked            matched an existing one (org nr, or the user chose it): only the source
 *                     link was written, the restaurant's own fields were left alone
 *   already_imported  this exact Fortnox customer was imported before; nothing written
 *   conflict          a weak or ambiguous match; nothing written, the user decides
 */
export interface FortnoxCustomerImportResult {
    action: 'created' | 'linked' | 'already_imported' | 'conflict';
    pushKey?: string;
    customer?: any;
    candidates?: FortnoxCustomerCandidate[];
    external?: ExternalCustomer;
    matchedOn?: 'organization_number' | 'user';
}

export interface FortnoxCustomerRefreshResult {
    action: 'refreshed';
    pushKey: string;
    /** Slash paths that actually changed, e.g. ['billingAddress/city']. */
    changed: string[];
    customer: any;
}

/** What an import may set on a NEW customer, as reviewed in the form (backend allowlist). */
export type FortnoxImportOverrides = { [path: string]: string | number };

// -- Sending an invoice to Fortnox (unbooked draft) -------------------------
//
// PubQ creates the invoice record in the restaurant's Fortnox and stops there: never booked,
// e-mailed or e-invoiced from here. Fortnox has no idempotency key, so the backend keeps the
// send state on the PubQ invoice itself and that state is what these types carry.

export type FortnoxInvoiceState =
    /** Never sent. */
    | 'none'
    /** Claimed in a transaction before the POST; a second click cannot create a second draft. */
    | 'sending'
    /** Fortnox has it, under `documentNumber`. A repeat is answered, never re-POSTed. */
    | 'sent'
    /** Fortnox refused it, so nothing was created. Safe to retry. */
    | 'failed'
    /**
     * The POST may or may not have created a draft (network error, or the write-back after a
     * successful POST failed). NEVER resent automatically: a person checks Fortnox first and
     * then confirms with the exact phrase the backend names.
     */
    | 'uncertain';

export interface FortnoxInvoiceReason {
    /** e.g. customer_not_linked, mapping_gap, reauth_required, invoices_disabled, already_sent. */
    code: string;
    message: string;
    /** On reauth_required: 'missing_scope' | 'token'. */
    reason?: string;
    extra?: { [key: string]: unknown };
}

/**
 * The gross PubQ charged against what Fortnox will total from ex-VAT rows.
 *
 * Fortnox sums each row's net to the öre and applies VAT per rate, so a couple of öre of drift is
 * inherent, not a bug. The backend warns above 1 öre and refuses above 1 kr (`amount_mismatch`).
 */
export interface FortnoxInvoiceReconciliation {
    /** PubQ's invoice amount, in kronor. */
    invoiceAmount: number | null;
    receiptTotal: number | null;
    /** What the Fortnox rows add up to, incl. VAT. */
    computedGross: number;
    /** computedGross minus the invoice amount, in kronor. */
    drift: number;
}

/** Can this invoice be sent right now, and if not, why. Never calls Fortnox. */
export interface FortnoxInvoiceReadiness {
    canSend: boolean;
    status: FortnoxInvoiceState;
    documentNumber: string | null;
    reasons: FortnoxInvoiceReason[];
    /** The connection has the `invoice` scope. */
    scopeReady: boolean;
    /** This deploy is allowed to create invoices at all (FORTNOXZ_ALLOW_INVOICES). */
    invoicesEnabled: boolean;
    mode: string | null;
    /** The invoice's corporate customer carries a Fortnox link. */
    customerLinked: boolean;
    reconciliation: FortnoxInvoiceReconciliation | null;
    /** Present when the only thing missing is consent: the Fortnox consent page. */
    reconnectUrl?: string | null;
}

/**
 * What a send attempt came to. Every one of these is a normal outcome the screen renders --
 * nothing here is thrown, because "the customer is not linked yet" is not an exception.
 */
export type FortnoxInvoiceSendOutcome =
    | { kind: 'sent'; documentNumber: string; warning: string | null; writeBackFailed: boolean }
    | { kind: 'already_sent'; documentNumber: string }
    /** A precondition the restaurant has to fix (link the customer, reconnect, map a VAT rate). */
    | { kind: 'blocked'; code: string; message: string; reason?: string; reconnectUrl?: string | null; extra?: { [key: string]: unknown } }
    /** Fortnox refused it. Nothing was created; retrying is safe. */
    | { kind: 'failed'; code: string; message: string }
    /** May or may not exist in Fortnox. Needs a human check, then an explicit resend. */
    | { kind: 'uncertain'; message: string; confirm: string | null };

/**
 * Codes that mean "fix something first" rather than "it broke". Kept as a set rather than a
 * status check because the backend answers 409 for several unrelated things.
 */
const INVOICE_BLOCKED_CODES = new Set([
    'customer_not_linked',
    'customer_company_mismatch',
    'receipt_not_found',
    'receipt_not_invoiceable',
    'currency_unsupported',
    'mapping_gap',
    'amount_mismatch',
    'invoices_disabled',
    'NOT_CONNECTED',
    'reauth_required',
    'send_in_progress',
    'invoice_not_found',
    'fortnox_unavailable',
]);

export class FortnoxZError extends Error {
    constructor(
        readonly status: number,
        readonly code: string,
        message: string,
        /** The backend's `extra` — carries `reconnectUrl` on a reauth_required. */
        readonly extra: { reconnectUrl?: string | null; reconnectBlocked?: string; reason?: string } | null = null,
    ) {
        super(message);
    }

    /** The connection needs to go through Fortnox consent again (new scope, or a dead token). */
    get needsReconnect(): boolean {
        return this.code === 'reauth_required';
    }
}

/** No request may leave the page waiting forever. */
const REQUEST_TIMEOUT_MS = 20000;

function withTimeout<T>(p: Promise<T>, ms: number, message: string): Promise<T> {
    let t: any;
    return Promise.race([
        p,
        new Promise<T>((_, reject) => {
            t = setTimeout(() => reject(new FortnoxZError(0, 'TIMEOUT', message)), ms);
        }),
    ]).finally(() => clearTimeout(t));
}

@Injectable({ providedIn: 'root' })
export class FortnoxZService {
    constructor(private http: HttpClient, private afAuth: AngularFireAuth, private authService: AuthService, private languageService: LanguageService) {}

    /** Error texts in the admin's language (language keys FZ_ERR_*). */
    private t(key: string, ...params: any[]): string {
        return this.languageService.getString(key, this.languageService.currentLanguage, params.length ? params.map(String) : undefined) || key;
    }

    /** Shown in the menu only where the backend exists (staging, until cutover). */
    get enabled(): boolean {
        return !!environment.fortnoxZApiUrl;
    }

    private async headers() {
        // afAuth.currentUser can stall before auth is ready; never wait on it forever.
        const signInTimeout = this.t('FZ_ERR_SIGNIN_TIMEOUT', 10);
        const user = await withTimeout(this.afAuth.currentUser, 10000, signInTimeout);
        const token = user ? await withTimeout(user.getIdToken(), 10000, signInTimeout) : (this.authService.currentUserSubject.value as any)?.accessToken;
        if (!token) throw new FortnoxZError(401, 'unauthenticated', this.t('FZ_ERR_NOT_SIGNED_IN'));
        return { Authorization: `Bearer ${token}` };
    }

    private url(route: string, path = '') {
        return `${environment.fortnoxZApiUrl}/locations/${encodeURIComponent(route)}${path}`;
    }

    private async call<T>(method: 'GET' | 'POST' | 'PUT', url: string, body?: any): Promise<T> {
        try {
            const headers = await this.headers();
            // A send/backfill books in Fortnox and may take longer; reads must be quick.
            const ms = method === 'GET' ? REQUEST_TIMEOUT_MS : 120000;
            if (method === 'GET') return await firstValueFrom(this.http.get<T>(url, { headers }).pipe(timeout(ms)));
            if (method === 'PUT') return await firstValueFrom(this.http.put<T>(url, body || {}, { headers }).pipe(timeout(ms)));
            return await firstValueFrom(this.http.post<T>(url, body || {}, { headers }).pipe(timeout(ms)));
        } catch (err) {
            if (err instanceof TimeoutError) throw new FortnoxZError(0, 'TIMEOUT', this.t('FZ_ERR_SERVICE_TIMEOUT'));
            if (err instanceof HttpErrorResponse) {
                if (err.status === 0) throw new FortnoxZError(0, 'UNREACHABLE', this.t('FZ_ERR_UNREACHABLE'));
                // Two 409s are normal results, not failures, and are handed back as data:
                // /send refusing to double-book, and an import whose match is ambiguous.
                if (err.status === 409 && err.error && err.error.refused) return err.error as T;
                if (err.status === 409 && err.error && err.error.action === 'conflict') return err.error as T;
                throw new FortnoxZError(err.status, err.error?.error || 'ERROR', err.error?.message || err.message, err.error?.extra || null);
            }
            throw err;
        }
    }

    state(route: string) {
        return this.call<FortnoxZState>('GET', this.url(route));
    }
    connect(route: string, confirmMismatch = false) {
        return this.call<FortnoxZState & { alreadyConnected?: boolean; authorizeUrl?: string }>('POST', this.url(route, '/connect'), { confirmMismatch, adminOrigin: window.location.origin });
    }
    disconnect(route: string, confirmOrgNo: string) {
        return this.call<FortnoxZState>('POST', this.url(route, '/disconnect'), { confirmOrgNo });
    }
    saveSettings(route: string, settings: Partial<FortnoxZState['settings']>) {
        return this.call<FortnoxZState>('PUT', this.url(route, '/settings'), settings);
    }
    lists(route: string, refresh = false) {
        return this.call<FortnoxZLists>('GET', this.url(route, `/lists${refresh ? '?refresh=1' : ''}`));
    }
    zreports(route: string) {
        return this.call<FortnoxZRow[]>('GET', this.url(route, '/zreports?limit=90'));
    }
    preview(route: string, zNo: number) {
        return this.call<FortnoxZPreview>('POST', this.url(route, `/zreports/${zNo}/preview`));
    }
    send(route: string, zNo: number, opts: { approve?: boolean; force?: boolean; confirm?: string } = {}) {
        return this.call<{ status: string; refused?: string; export: any }>('POST', this.url(route, `/zreports/${zNo}/send`), opts);
    }
    backfill(route: string, fromZNo: number, toZNo: number) {
        return this.call<{ zNo: number; status: string; refused: string | null; error: string | null; fortnox: any }[]>('POST', this.url(route, '/backfill'), { fromZNo, toZNo });
    }

    // -- Customer import (consume-only) --------------------------------------

    /**
     * Which sources this restaurant could import customers from, and whether each is usable.
     * Cheap and side-effect free, so the create form can call it on open.
     */
    customerSources(route: string) {
        return this.call<FortnoxCustomerSourceStatus[]>('GET', this.url(route, '/customer-sources'));
    }

    /** Search the linked Fortnox company's customer register. Needs >= 2 characters. */
    searchCustomers(route: string, q: string, opts: { source?: string; includeInactive?: boolean } = {}) {
        const params = new URLSearchParams({ q });
        if (opts.source) params.set('source', opts.source);
        if (opts.includeInactive) params.set('inactive', '1');
        // The consent page needs to know which admin origin to come back to.
        params.set('adminOrigin', window.location.origin);
        return this.call<{ source: string; results: ExternalCustomer[] }>('GET', this.url(route, `/customers?${params.toString()}`));
    }

    /** The full source record, what PubQ would store, and the dedupe verdict. Writes nothing. */
    customerDetail(route: string, source: string, externalId: string) {
        const params = new URLSearchParams({ adminOrigin: window.location.origin });
        return this.call<FortnoxCustomerDetail>(
            'GET',
            this.url(route, `/customers/${encodeURIComponent(source)}/${encodeURIComponent(externalId)}?${params.toString()}`),
        );
    }

    /**
     * Import the picked customer. `resolution` is only consulted when the match was a conflict:
     * 'create' makes a second PubQ customer, 'link' + linkPushKey attaches the source link to the
     * one the user chose. Without it a conflict comes back as `action: 'conflict'`.
     */
    importCustomer(
        route: string,
        body: { source: string; externalId: string; resolution?: 'create' | 'link'; linkPushKey?: string; overrides?: FortnoxImportOverrides },
    ) {
        return this.call<FortnoxCustomerImportResult>('POST', this.url(route, '/customers/import'), { ...body, adminOrigin: window.location.origin });
    }

    /** Re-read the linked source customer and bring the source-owned fields up to date. */
    refreshCustomer(route: string, pushKey: string) {
        return this.call<FortnoxCustomerRefreshResult>('POST', this.url(route, `/customers/${encodeURIComponent(pushKey)}/refresh`), {
            adminOrigin: window.location.origin,
        });
    }

    // -- Sending an invoice to Fortnox ---------------------------------------

    /**
     * Readiness for one invoice. Cheap: it reads PubQ's own data and the stored connection, and
     * never calls Fortnox, so the list and the detail view can both ask freely.
     */
    invoiceFortnoxStatus(route: string, invoiceId: string) {
        const params = new URLSearchParams({ adminOrigin: window.location.origin });
        return this.call<FortnoxInvoiceReadiness>(
            'GET',
            this.url(route, `/invoices/${encodeURIComponent(invoiceId)}/fortnox-status?${params.toString()}`),
        );
    }

    /**
     * Create the draft in Fortnox.
     *
     * Every expected refusal comes back as an outcome rather than an exception: a 412 for an
     * unlinked customer and a 409 for a missing scope are things the screen explains, not errors
     * to log. Only the unexpected (a 500, a timeout, an unreachable service) still throws.
     *
     * `force` + `confirm` is the explicit resend for an `uncertain` send, and the phrase has to be
     * exactly the one the backend asked for.
     */
    async sendInvoiceToFortnox(
        route: string,
        invoiceId: string,
        opts: { force?: boolean; confirm?: string } = {},
    ): Promise<FortnoxInvoiceSendOutcome> {
        const body: { [k: string]: unknown } = { adminOrigin: window.location.origin };
        if (opts.force) body['force'] = true;
        if (opts.confirm) body['confirm'] = opts.confirm;
        try {
            const r = await this.call<{ action: 'sent' | 'already_sent'; documentNumber: string; warning?: string | null; writeBackFailed?: boolean }>(
                'POST',
                this.url(route, `/invoices/${encodeURIComponent(invoiceId)}/send`),
                body,
            );
            if (r.action === 'already_sent') return { kind: 'already_sent', documentNumber: r.documentNumber };
            return {
                kind: 'sent',
                documentNumber: r.documentNumber,
                warning: r.warning ?? null,
                writeBackFailed: !!r.writeBackFailed,
            };
        } catch (err) {
            if (!(err instanceof FortnoxZError)) throw err;
            if (err.code === 'send_uncertain') {
                return { kind: 'uncertain', message: err.message, confirm: (err.extra as any)?.confirm ?? null };
            }
            if (err.code === 'already_sent') {
                // Only reachable with force: the backend refuses to send a sent invoice twice.
                const n = (err.extra as any)?.documentNumber;
                if (n) return { kind: 'already_sent', documentNumber: String(n) };
            }
            if (err.code === 'reauth_required') {
                return { kind: 'blocked', code: err.code, message: err.message, reason: err.extra?.reason, reconnectUrl: err.extra?.reconnectUrl ?? null };
            }
            if (INVOICE_BLOCKED_CODES.has(err.code)) {
                return { kind: 'blocked', code: err.code, message: err.message, extra: err.extra as any };
            }
            // fortnox_rejected (502) and anything else Fortnox said no to: nothing was created.
            return { kind: 'failed', code: err.code, message: err.message };
        }
    }
}
